// src/hooks/useMcpRegistry.ts

import React from 'react';
import {
  toItems,
  useCreateMcpCapabilityMutation,
  useCreateMcpServerAuthMutation,
  useCreateMcpServerMutation,
  useDeleteMcpCapabilityMutation,
  useDeleteMcpServerAuthMutation,
  useDeleteMcpServerMutation,
  useEvaluateMcpCapabilityMutation,
  useFetchMcpConfiguredCapabilities,
  useFetchMcpDiscoveredCapabilities,
  useFetchMcpServer,
  useFetchMcpServerAuthConfigs,
  useFetchMcpServerHealthRows,
  useFetchMcpServers,
  useFetchMcpServerSyncState,
  useTriggerMcpDiscoverySyncMutation,
  useUpdateMcpCapabilityMutation,
  useUpdateMcpServerAuthMutation,
  useUpdateMcpServerMutation,
} from './ai-hooks';
import {
  ID,
  McpConfiguredCapability,
  McpDiscoveredCapability,
  McpMergedCapability,
} from '../types/aiTypes';

const REDACTED_VALUES = new Set(['[stored securely]', '[redacted]']);

function stripRedactedCredentialValues(auth: any) {
  const credential = { ...(auth?.credential || {}) };

  Object.keys(credential).forEach((key) => {
    if (REDACTED_VALUES.has(String(credential[key] || '').trim())) {
      delete credential[key];
    }
  });

  return {
    ...auth,
    credential,
  };
}

function capabilityKey(kind?: string, name?: string) {
  return `${kind || 'unknown'}::${name || ''}`;
}

function safeJsonValue(value: any): any {
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'object') return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  return value;
}

function getDiscoveredInputSchema(discovered: any): any {
  return (
    safeJsonValue(discovered?.input_schema) ||
    safeJsonValue(discovered?.input_schema_json) ||
    safeJsonValue(discovered?.raw_payload_json?.inputSchema) ||
    safeJsonValue(discovered?.raw_payload_json?.input_schema) ||
    safeJsonValue(discovered?.raw_payload_json?.arguments) ||
    safeJsonValue(discovered?.raw?.inputSchema) ||
    safeJsonValue(discovered?.raw?.input_schema) ||
    safeJsonValue(discovered?.raw?.arguments) ||
    undefined
  );
}

function getDiscoveredOutputSchema(discovered: any): any {
  return (
    safeJsonValue(discovered?.output_schema) ||
    safeJsonValue(discovered?.output_schema_json) ||
    safeJsonValue(discovered?.raw_payload_json?.outputSchema) ||
    safeJsonValue(discovered?.raw_payload_json?.output_schema) ||
    safeJsonValue(discovered?.raw?.outputSchema) ||
    safeJsonValue(discovered?.raw?.output_schema) ||
    undefined
  );
}

function getDiscoveredRaw(discovered: any): any {
  return (
    safeJsonValue(discovered?.raw_payload_json) ||
    safeJsonValue(discovered?.raw) ||
    discovered
  );
}

function normalizeDiscoveredCapability(row: any): McpDiscoveredCapability {
  const raw = getDiscoveredRaw(row);
  const inputSchema = getDiscoveredInputSchema(row);
  const outputSchema = getDiscoveredOutputSchema(row);

  return {
    ...row,
    id: row?.id,
    mcp_server: row?.mcp_server || row?.mcp_server_id,
    capability_kind: row?.capability_kind || row?.capability_type || raw?.capability_kind || raw?.type || 'tool',
    name: row?.name || row?.capability_name || raw?.name || '',
    description: row?.description || raw?.description || '',
    input_schema: inputSchema,
    output_schema: outputSchema,
    uri_template:
      row?.uri_template ||
      row?.uriTemplate ||
      raw?.uri_template ||
      raw?.uriTemplate ||
      raw?.uri ||
      null,
    enabled: row?.enabled !== false,
    raw,
  };
}

function mergeCapabilities(
  configured: McpConfiguredCapability[],
  discoveredRaw: any[]
): McpMergedCapability[] {
  const discovered = discoveredRaw.map(normalizeDiscoveredCapability);

  const configuredMap = new Map<string, McpConfiguredCapability>();
  const discoveredMap = new Map<string, McpDiscoveredCapability>();

  configured.forEach((cap) => {
    configuredMap.set(
      capabilityKey(cap.capability_type, cap.capability_name),
      cap
    );
  });

  discovered.forEach((cap) => {
    discoveredMap.set(capabilityKey(cap.capability_kind, cap.name), cap);
  });

  const keys = new Set<string>([
    ...Array.from(configuredMap.keys()),
    ...Array.from(discoveredMap.keys()),
  ]);

  return Array.from(keys)
    .map((key) => {
      const cfg = configuredMap.get(key);
      const disc = discoveredMap.get(key);

      const kind = (cfg?.capability_type ||
        disc?.capability_kind ||
        'tool') as any;

      const name = cfg?.capability_name || disc?.name || '';

      return {
        key,
        id: cfg?.id || disc?.id,
        display_name: cfg?.display_name || disc?.name || name,
        capability_name: name,
        name,
        description: disc?.description || cfg?.description || '',
        capability_type: kind,
        capability_kind: kind,
        configured: !!cfg,
        discovered: !!disc,
        allowed: Boolean((cfg as any)?.allowed ?? true),
        enabled: Boolean(cfg?.enabled ?? disc?.enabled ?? true),
        uri_template: disc?.uri_template || null,
        input_schema: disc?.input_schema,
        output_schema: disc?.output_schema,
        raw_configured: cfg,
        raw_discovered: disc,
      };
    })
    .sort((a, b) => {
      if (a.configured !== b.configured) return a.configured ? -1 : 1;
      return String(a.display_name || '').localeCompare(String(b.display_name || ''));
    });
}

function unwrapServer(resp: any) {
  if (!resp) return null;
  if (resp?.id) return resp;
  if (resp?.data?.id) return resp.data;
  return resp;
}

function deriveEffectiveAuthType(server: any, authConfigs: any[]) {
  const enabledAuth = authConfigs.find((auth) => auth?.auth_type && auth.auth_type !== 'none');
  return enabledAuth?.auth_type || server?.auth_type || 'none';
}

export function useMcpRegistry() {
  const [selectedServerId, setSelectedServerId] = React.useState<ID | null>(
    null
  );

  const [selectedCapability, setSelectedCapability] =
    React.useState<McpMergedCapability | null>(null);

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorInitialValue, setEditorInitialValue] =
    React.useState<any | null>(null);

  const {
    data: serversResp,
    refetch: refetchServers,
    isFetching: serversLoading,
  } = useFetchMcpServers();

  const baseServers = React.useMemo<any[]>(
    () => toItems(serversResp),
    [serversResp]
  );

  const selectedListServer = React.useMemo(() => {
    if (!selectedServerId) return null;
    return (
      baseServers.find((server: any) => String(server.id) === String(selectedServerId)) ||
      null
    );
  }, [baseServers, selectedServerId]);

  const { data: serverResp, refetch: refetchServer } = useFetchMcpServer(
    selectedServerId,
    {
      enabled: !!selectedServerId,
    }
  );

  const rawServer = React.useMemo(() => unwrapServer(serverResp), [serverResp]);

  const { data: authResp, refetch: refetchAuth } =
    useFetchMcpServerAuthConfigs(selectedServerId, {
      enabled: !!selectedServerId,
    });

  const { data: configuredResp, refetch: refetchConfigured } =
    useFetchMcpConfiguredCapabilities(selectedServerId, {
      enabled: !!selectedServerId,
    });

  const { data: discoveredResp, refetch: refetchDiscovered } =
    useFetchMcpDiscoveredCapabilities(selectedServerId, {
      enabled: !!selectedServerId,
    });

  const { data: healthResp, refetch: refetchHealth } =
    useFetchMcpServerHealthRows(selectedServerId, {
      enabled: !!selectedServerId,
    });

  const { data: syncResp, refetch: refetchSync } =
    useFetchMcpServerSyncState(selectedServerId, {
      enabled: !!selectedServerId,
    });

  const authConfigs = React.useMemo<any[]>(() => {
    const direct = toItems(authResp);

    if (direct.length) return direct;

    if (
      selectedListServer &&
      Array.isArray(selectedListServer.auth_configs) &&
      selectedListServer.auth_configs.length
    ) {
      return selectedListServer.auth_configs;
    }

    if (rawServer && Array.isArray(rawServer.auth_configs)) {
      return rawServer.auth_configs;
    }

    return [];
  }, [authResp, rawServer, selectedListServer]);

  const configured = React.useMemo<McpConfiguredCapability[]>(() => {
    const direct = toItems<McpConfiguredCapability>(configuredResp);

    if (direct.length) return direct;

    if (
      selectedListServer &&
      Array.isArray(selectedListServer.capabilities) &&
      selectedListServer.capabilities.length
    ) {
      return selectedListServer.capabilities;
    }

    if (rawServer && Array.isArray(rawServer.capabilities)) {
      return rawServer.capabilities;
    }

    return [];
  }, [configuredResp, rawServer, selectedListServer]);

  const discovered = React.useMemo<any[]>(
    () => toItems(discoveredResp),
    [discoveredResp]
  );

  const healthRows = React.useMemo<any[]>(() => {
    const rows = toItems(healthResp);

    if (rows.length) return rows;

    if (selectedListServer?.latest_health) {
      return [selectedListServer.latest_health];
    }

    if (rawServer?.latest_health) {
      return [rawServer.latest_health];
    }

    return [];
  }, [healthResp, rawServer, selectedListServer]);

  const syncRows = React.useMemo<any[]>(
    () => toItems(syncResp),
    [syncResp]
  );

  const latestHealth = healthRows[0] || null;
  const syncState = syncRows[0] || null;

  const server = React.useMemo(() => {
    const merged = {
      ...(selectedListServer || {}),
      ...(rawServer || {}),
    };

    if (!merged?.id && !selectedListServer?.id && !rawServer?.id) {
      return null;
    }

    const effectiveAuthType = deriveEffectiveAuthType(merged, authConfigs);

    return {
      ...merged,
      auth_configs: authConfigs,
      capabilities: configured,
      auth_type: effectiveAuthType,
      health_status:
        latestHealth?.status ||
        merged.health_status ||
        selectedListServer?.health_status ||
        'unknown',
      configured_count:
        typeof merged.configured_count === 'number'
          ? merged.configured_count
          : configured.length,
      discovered_count:
        typeof merged.discovered_count === 'number'
          ? merged.discovered_count
          : discovered.length,
    };
  }, [
    authConfigs,
    configured,
    discovered.length,
    latestHealth?.status,
    rawServer,
    selectedListServer,
  ]);

  const capabilities = React.useMemo(
    () => mergeCapabilities(configured, discovered),
    [configured, discovered]
  );

  const servers = React.useMemo(() => {
    return baseServers.map((item: any) => {
      if (selectedServerId && String(item.id) === String(selectedServerId)) {
        return {
          ...item,
          ...(server || {}),
          health_status:
            latestHealth?.status ||
            server?.health_status ||
            item.health_status ||
            'unknown',
          configured_count:
            typeof item.configured_count === 'number'
              ? item.configured_count
              : configured.length,
          discovered_count:
            typeof item.discovered_count === 'number'
              ? item.discovered_count
              : discovered.length,
        };
      }

      return item;
    });
  }, [
    baseServers,
    configured.length,
    discovered.length,
    latestHealth?.status,
    selectedServerId,
    server,
  ]);

  const createServer = useCreateMcpServerMutation();
  const updateServer = useUpdateMcpServerMutation();
  const deleteServer = useDeleteMcpServerMutation();

  const createAuth = useCreateMcpServerAuthMutation();
  const updateAuth = useUpdateMcpServerAuthMutation();
  const deleteAuth = useDeleteMcpServerAuthMutation();

  const createCapability = useCreateMcpCapabilityMutation();
  const updateCapability = useUpdateMcpCapabilityMutation();
  const deleteCapability = useDeleteMcpCapabilityMutation();

  const triggerSync = useTriggerMcpDiscoverySyncMutation();
  const evaluateCapability = useEvaluateMcpCapabilityMutation();

  const refreshAll = React.useCallback(async () => {
    await refetchServers();

    if (selectedServerId) {
      await Promise.all([
        refetchServer(),
        refetchAuth(),
        refetchConfigured(),
        refetchDiscovered(),
        refetchHealth(),
        refetchSync(),
      ]);
    }
  }, [
    refetchAuth,
    refetchConfigured,
    refetchDiscovered,
    refetchHealth,
    refetchServer,
    refetchServers,
    refetchSync,
    selectedServerId,
  ]);

  const openCreate = React.useCallback(() => {
    setEditorInitialValue(null);
    setEditorOpen(true);
  }, []);

  const openEdit = React.useCallback(
    (row: any) => {
      const hydrated = {
        ...row,
        ...(server && String(server.id) === String(row.id) ? server : {}),
        auth_configs:
          String(selectedServerId) === String(row.id) ? authConfigs : row.auth_configs || [],
        capabilities:
          String(selectedServerId) === String(row.id) ? configured : row.capabilities || [],
      };

      setEditorInitialValue(hydrated);
      setEditorOpen(true);
    },
    [authConfigs, configured, selectedServerId, server]
  );

  const closeEditor = React.useCallback(() => {
    setEditorOpen(false);
    setEditorInitialValue(null);
  }, []);

  const saveServer = React.useCallback(
    async (payload: any) => {
      const {
        auth_configs = [],
        capabilities: incomingCapabilities = [],
        ...serverPayload
      } = payload || {};

      let serverId: ID | null = serverPayload.id || null;

      if (serverId) {
        await updateServer.mutateAsync(serverPayload);
      } else {
        const created: any = await createServer.mutateAsync(serverPayload);
        serverId = created?.id || created?.data?.id || null;
      }

      if (!serverId) {
        closeEditor();
        await refetchServers();
        return;
      }

      const existingAuthById = new Map<string, any>(
        authConfigs
          .filter((auth: any) => auth?.id)
          .map((auth: any) => [String(auth.id), auth] as [string, any])
      );

      const incomingAuthIds = new Set<string>();

      for (const auth of auth_configs) {
        const cleaned = stripRedactedCredentialValues(auth);

        if (auth?.id) {
          incomingAuthIds.add(String(auth.id));

          await updateAuth.mutateAsync({
            id: auth.id,
            mcp_server: serverId,
            auth_type: cleaned.auth_type,
            credential: cleaned.credential || {},
          });
        } else {
          await createAuth.mutateAsync({
            mcp_server: serverId,
            auth_type: cleaned.auth_type,
            credential: cleaned.credential || {},
          });
        }
      }

      for (const [id] of existingAuthById) {
        if (!incomingAuthIds.has(id)) {
          await deleteAuth.mutateAsync(id);
        }
      }

      const existingCapsById = new Map<string, any>(
        configured
          .filter((cap: any) => cap?.id)
          .map((cap: any) => [String(cap.id), cap] as [string, any])
      );

      const incomingCapIds = new Set<string>();

      for (const cap of incomingCapabilities) {
        if (cap?.id) {
          incomingCapIds.add(String(cap.id));

          await updateCapability.mutateAsync({
            ...cap,
            mcp_server: serverId,
          });
        } else {
          await createCapability.mutateAsync({
            ...cap,
            mcp_server: serverId,
          });
        }
      }

      for (const [id] of existingCapsById) {
        if (!incomingCapIds.has(id)) {
          await deleteCapability.mutateAsync(id);
        }
      }

      setSelectedServerId(serverId);
      closeEditor();
      await refreshAll();
    },
    [
      authConfigs,
      closeEditor,
      configured,
      createAuth,
      createCapability,
      createServer,
      deleteAuth,
      deleteCapability,
      refetchServers,
      refreshAll,
      updateAuth,
      updateCapability,
      updateServer,
    ]
  );

  const removeServer = React.useCallback(
    async (serverId: ID) => {
      if (!window.confirm('Delete this MCP server?')) return;

      await deleteServer.mutateAsync(serverId);

      if (String(selectedServerId) === String(serverId)) {
        setSelectedServerId(null);
        setSelectedCapability(null);
      }

      await refetchServers();
    },
    [deleteServer, refetchServers, selectedServerId]
  );

  const syncSelected = React.useCallback(async () => {
    if (!selectedServerId) return;

    await triggerSync.mutateAsync(selectedServerId);
    await refreshAll();
  }, [refreshAll, selectedServerId, triggerSync]);

  const promoteDiscovered = React.useCallback(
    async (capability: McpMergedCapability) => {
      if (!selectedServerId) return;

      await createCapability.mutateAsync({
        mcp_server: selectedServerId,
        capability_type:
          capability.capability_kind || capability.capability_type || 'tool',
        capability_name: capability.name || capability.capability_name,
        display_name:
          capability.display_name ||
          capability.name ||
          capability.capability_name,
        description: capability.description || '',
        allowed: true,
        enabled: true,
        config_json: {},
      });

      await refetchConfigured();
    },
    [createCapability, refetchConfigured, selectedServerId]
  );

  const selectServer = React.useCallback((serverId: ID) => {
    setSelectedServerId(serverId);
    setSelectedCapability(null);
  }, []);

  return {
    servers,
    serversLoading,
    selectedServerId,
    selectServer,

    server,
    authConfigs,
    capabilities,
    selectedCapability,
    setSelectedCapability,
    latestHealth,
    syncState,

    editorOpen,
    editorInitialValue,
    openCreate,
    openEdit,
    closeEditor,
    saveServer,

    removeServer,
    refreshAll,
    syncSelected,
    promoteDiscovered,

    evaluateCapability,

    saving:
      createServer.isPending ||
      updateServer.isPending ||
      createAuth.isPending ||
      updateAuth.isPending ||
      createCapability.isPending ||
      updateCapability.isPending,

    syncing: triggerSync.isPending,
    promoting: createCapability.isPending,
  };
}